import { Browser } from '@capacitor/browser';
import { AppLauncher } from '@capacitor/app-launcher';
import { InAppBrowser, ToolBarType, BackgroundColor } from '@capgo/inappbrowser';
import { isNativeNotWeb } from '@/utils/platform-check';
import mixpanel from '@/utils/mixpanel';

// Chrome-less embedded WebView (design direction Phase 2): no URL bar,
// no browser furniture — the page draws its own header and closes via
// window.mobileApp.close().
//
// This is the GenS Translate branded origin — the redirect DESTINATION.
// Never point this at a Railway environment URL: those are per-environment
// and would ship a tester/dev backend to the App Store.
export const TRANSLATE_ORIGIN = 'https://translate.gens.app';

// The URL the app actually opens: a short.io link that 302s to
// TRANSLATE_ORIGIN with query params forwarded, so the translate site can be
// repointed without an app release. The /listener suffix is its own short.io
// link (slug "interpretation/listener") — short.io does NOT forward unknown
// path suffixes; a missing slug falls through to the domain default and the
// guard below bounces the user to the system browser.
export const INTERPRETATION_SHORTLINK = 'https://gens.app/interpretation';
const SHORTLINK_ORIGIN = 'https://gens.app';

// Both hops of the redirect chain must stay inside the WebView: the shortlink
// origin (pre-302) and the translate origin (destination). A mismatch here
// silently bounces every navigation out to Safari.
const ALLOWED_ORIGINS = [TRANSLATE_ORIGIN, SHORTLINK_ORIGIN];

// Deep-link value -> shortlink suffix. Deep links carry untrusted values;
// anything outside this allowlist falls back to 'home'. The bare shortlink
// lands on the translate site's /home. Suffixes are deliberately unversioned:
// the short.io slug owns the versioned destination (e.g. "interpretation/converse"
// -> translate.gens.app/converse-v2), so printed QRs and shipped binaries
// survive a site version bump.
const PAGE_PATHS = { home: '', listener: '/listener', converse: '/converse' };

// Channel attribution allowlist — src arrives on attacker-craftable URLs and
// feeds Mixpanel, so unknown values collapse to 'other'.
const KNOWN_SRC = ['qr', 'push', 'cms'];

export const resolveInterpretationPage = value => {
  // vue-router yields arrays for duplicated query keys, and a bare
  // hasOwnProperty lookup would coerce ['listener'] to 'listener' — normalize
  // to the first element and require a string before the allowlist check.
  const v = Array.isArray(value) ? value[0] : value;
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(PAGE_PATHS, v) ? v : 'home';
};

export const resolveSrc = value => {
  if (KNOWN_SRC.includes(value)) return value;
  return value === null || value === undefined ? null : 'other';
};

// A chrome-less WebView has no back button: an external link (e.g. the
// listener's BreezeTranslate fallback banner) would strand the user on a
// foreign site. Bounce anything off-origin out to the system browser.
// Origin equality, not startsWith: 'https://translate.gens.app.evil.tld' and
// userinfo-trick URLs share the string prefix but must bounce.
//
// Memoize the addListener PROMISE, not the handle: a check-then-act across an
// await would let two concurrent first calls (deep link racing the Home
// button) register two listeners, double-bouncing every off-origin event.
// NOTE: the guard lives for the whole app session, even after the WebView
// closes. Any FUTURE feature opening an InAppBrowser webview at another
// origin will be closed and bounced by this guard — revisit before adding a
// second @capgo/inappbrowser consumer.
let interpUrlGuardPromise = null;

const isAllowedOrigin = url => {
  try {
    return ALLOWED_ORIGINS.includes(new URL(url).origin);
  } catch {
    // Unparsable URLs bounce rather than staying chrome-less
    return false;
  }
};

// livevoice.io/listen/* is a registered universal link (iOS AASA) and
// verified app link (Android assetlinks) for the LiveVoice app — the Services
// tile on the translate site hands live interpretation off to it.
// Browser.open() presents SFSafariViewController / Custom Tabs, and a
// programmatic browser launch never dispatches universal/app links, so
// listeners would land on LiveVoice's web page even with the app installed.
// AppLauncher.openUrl() routes through UIApplication.open / ACTION_VIEW,
// which opens the app when installed and the default browser otherwise.
// https only: app-link dispatch is registered for https, so http would open
// a browser regardless — let it take the normal bounce path.
const APP_LINK_HOSTS = ['livevoice.io', 'www.livevoice.io'];

const isAppLink = parsed =>
  parsed.protocol === 'https:' && APP_LINK_HOSTS.includes(parsed.hostname);

// webNavigate is a test seam only: jsdom (26+) makes window.location
// unforgeable, so the web branch cannot be observed without injection.
// Production callers never pass it.
const OPEN_TIMEOUT_MS = 15000;

// Serializes concurrent opens across ALL callers: the Home watcher's in-flight
// flag only guards the watcher, so a button double-tap (or a button racing a
// deep link) would otherwise defensively close the just-opened WebView and
// reopen it. Concurrent second calls drop — same policy as the watcher.
let openInFlight = false;

// Set once an off-origin URL has been handed to another app for THIS WebView
// session; reset when the next session opens. Deliberately NOT an in-flight
// lock: nothing awaits on it, so a hung plugin call can never pin it and
// starve the guard (see the openInFlight timeout above for that hazard).
let handedOffToExternalApp = false;

const buildInterpretationUrl = (page, source, conferencePassCode) => {
  const params = new URLSearchParams({ source, brand: 'gens' });
  if (typeof conferencePassCode === 'string' && conferencePassCode !== '') {
    params.set('conferencePassCode', conferencePassCode);
  }
  return `${INTERPRETATION_SHORTLINK}${PAGE_PATHS[page]}?${params.toString()}`;
};

export async function openInterpretationWebView(
  page = 'home',
  { src = null, conferencePassCode = null, webNavigate } = {},
) {
  const safePage = resolveInterpretationPage(page);
  // Defense in depth: callers sanitize src too, but the util owns the
  // invariant — attacker-craftable values must never reach Mixpanel raw.
  const safeSrc = resolveSrc(src);

  const track = () => {
    try {
      if (safeSrc !== null) {
        mixpanel.track('Interpretation Deeplink Opened', { page: safePage, src: safeSrc });
      }
    } catch {
      // noop
    }
  };

  if (!isNativeNotWeb()) {
    // Track BEFORE the redirect — navigation tears down this JS context.
    track();
    // source=web, not app: with source=app the translate site renders a close
    // control wired to window.mobileApp.close(), which doesn't exist in a
    // real browser.
    const url = buildInterpretationUrl(safePage, 'web', conferencePassCode);
    if (webNavigate) {
      webNavigate(url);
    } else {
      window.location.href = url;
    }
    return;
  }

  if (openInFlight) return;
  openInFlight = true;
  // A new WebView session: any handoff belonged to the previous one, so
  // re-arm the guard. Scoped per session, not per app run — otherwise the
  // first LiveVoice handoff would disable bouncing until app restart.
  handedOffToExternalApp = false;
  try {
    if (!interpUrlGuardPromise) {
      interpUrlGuardPromise = InAppBrowser.addListener('urlChangeEvent', async e => {
        if (!e || !e.url || isAllowedOrigin(e.url)) return;
        // about:blank is a neutral interstitial during in-WebView popup
        // handling (openBlankTargetInWebView) — closing on it would tear down
        // live interpretation sessions mid-service. Its content is
        // opener-scoped (first-party), so letting it pass is safe.
        if (e.url === 'about:blank') return;
        try {
          await InAppBrowser.close();
        } catch {
          // noop — already closed (e.g. rapid successive off-origin events)
        }
        // The plugin emits urlChangeEvent from KVO on webView.url, so ONE
        // logical navigation fires this guard more than once (provisional ->
        // commit, plus any redirect). Once we've handed off to another app the
        // WebView is closing and GenS is leaving the foreground, so those
        // trailing events are teardown noise — and iOS refuses
        // UIApplication.open from a backgrounded app, so their launch reports
        // completed:false and falls into the Browser.open fallback below,
        // stranding an SFSafariViewController behind the app we just opened.
        // The close above still runs on every event, so the chrome-less
        // escape hatch is unchanged; only the re-launch/re-bounce is skipped.
        if (handedOffToExternalApp) return;
        // Only http(s) goes to the system browser: intent://, market://, and
        // custom schemes would deep-link arbitrary installed apps on Android
        // without the confirmation a real browser shows. Anything else
        // (including unparsable URLs) is closed and dropped.
        let parsed = null;
        try {
          parsed = new URL(e.url);
        } catch {
          // unparsable — nothing could open it anyway
        }
        if (parsed && (parsed.protocol === 'http:' || parsed.protocol === 'https:')) {
          try {
            if (isAppLink(parsed)) {
              try {
                const { completed } = await AppLauncher.openUrl({ url: e.url });
                if (completed) {
                  handedOffToExternalApp = true;
                  return;
                }
              } catch {
                // A failed app launch must not strand the user with nothing —
                // fall through to the browser bounce.
              }
            }
            await Browser.open({ url: e.url });
          } catch (err) {
            // Never let a bounce failure become an unhandled rejection.
            console.error('interpretation guard bounce failed', err);
          }
        }
      }).catch(err => {
        // Registration failed — reset the memo so the next open re-registers.
        // A permanently rejected promise here would kill every future open
        // (deep link AND button) until app restart.
        interpUrlGuardPromise = null;
        throw err;
      });
    }
    await interpUrlGuardPromise;

    // A deep link can arrive while the WebView is already open (App.vue's
    // appUrlOpen handler closes the Capacitor Browser, not this plugin) —
    // close defensively so we never stack a second WebView.
    try {
      await InAppBrowser.close();
    } catch {
      // noop — nothing was open
    }

    // A stalled page load (captive portal, venue wifi, site down) can leave
    // the plugin promise unsettled forever with isPresentAfterPageLoad — and a
    // never-settling await here would pin openInFlight true, silently killing
    // the feature (button included) for the rest of the session. Time out,
    // reject (toast fires upstream), and let finally release the flag.
    let openTimeoutId;
    const openPromise = InAppBrowser.openWebView({
      url: buildInterpretationUrl(safePage, 'app', conferencePassCode),
      toolbarType: ToolBarType.BLANK,
      isPresentAfterPageLoad: true,
      backgroundColor: BackgroundColor.WHITE,
      // Page paints edge-to-edge (viewport-fit=cover + safe-area padding);
      // the plugin's default top margin shows as a dead black band otherwise.
      enabledSafeTopMargin: false,
      // target=_blank must load in THIS WebView, where the off-origin guard
      // sees it — the plugin's managed popups (Android default) bypass
      // urlChangeEvent entirely and would render foreign sites chrome-less
      // with no back button. iOS popup behavior needs device QA.
      openBlankTargetInWebView: true,
    });
    // Keep the abandoned promise observed: if it rejects AFTER the timeout
    // already won the race, that must not become an unhandled rejection.
    openPromise.catch(() => {
      // noop — the race already surfaced the failure path
    });
    try {
      await Promise.race([
        openPromise,
        new Promise((_, reject) => {
          openTimeoutId = setTimeout(
            () => reject(new Error('interpretation open timed out')),
            OPEN_TIMEOUT_MS,
          );
        }),
      ]);
    } finally {
      clearTimeout(openTimeoutId);
    }

    // Track AFTER a successful native open: a failed open must not count —
    // conference-day dashboards read this event. (Web tracks pre-redirect
    // because the navigation tears down the JS context.)
    track();
  } finally {
    openInFlight = false;
  }
}
